import { useCallback, useEffect, useState } from "react";
import { Alert, Box } from "@dealer-center/core-components";
import { useNrtRecords } from "@dealer-center/ddc-pages-nrt-database-filter-tool-fetch";
import type { NrtRecord } from "@dealer-center/ddc-pages-nrt-database-filter-tool-interface";
import { Header } from "./Display/Header";
import { RecordModal } from "./Display/RecordModal";
import { RecordsTable } from "./Display/RecordsTable";
import { SR_ONLY_SX } from "./Display/srOnlySx";

const PAGE_TITLE = "NRT Mapping Tool";

// `record` is only set in edit mode; closing drops it so the next open starts
// from a clean form.
type ModalState =
  | { isOpen: false }
  | { isOpen: true; mode: "add" }
  | { isOpen: true; mode: "edit"; record: NrtRecord };

export const PagesNrtDatabaseFilterToolUi = () => {
  const { data, error, isLoading } = useNrtRecords();
  const records = data ?? [];
  const [modalState, setModalState] = useState<ModalState>({ isOpen: false });

  useEffect(() => {
    document.title = PAGE_TITLE;
  }, []);

  const openAddModal = useCallback(
    () => setModalState({ isOpen: true, mode: "add" }),
    []
  );

  const openEditModal = useCallback(
    (record: NrtRecord) =>
      setModalState({ isOpen: true, mode: "edit", record }),
    []
  );

  const closeModal = useCallback(() => setModalState({ isOpen: false }), []);

  // The table's spinner and record count are silent to a screen reader.
  const statusAnnouncement = isLoading
    ? "Loading mapping records"
    : `${records.length} mapping records loaded`;

  return (
    // minWidth: 0 lets flex children shrink below their content width.
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: "1.5rem",
        minWidth: 0
      }}
    >
      <Header
        subtitle="View, edit, and manage data mappings"
        title={PAGE_TITLE}
        onAddNewRecord={openAddModal}
      />
      <Box
        aria-atomic="true"
        aria-live="polite"
        data-testid="nrt-records-live-region"
        role="status"
        sx={SR_ONLY_SX}
      >
        {statusAnnouncement}
      </Box>
      {error && (
        <Alert role="alert" type="error">
          {error.message || "Failed to load NRT records. Please try again."}
        </Alert>
      )}
      {/* Kept alongside the error: a failed refetch shouldn't blank the table. */}
      {!(error && records.length === 0) && (
        // isLoading only; isFetching is also true on background refetches.
        <RecordsTable
          isLoading={isLoading}
          records={records}
          onEditRecord={openEditModal}
        />
      )}
      {/* Unmounted when closed, so the form state resets between opens. */}
      {modalState.isOpen && (
        <RecordModal
          initialValues={
            modalState.mode === "edit" ? modalState.record : undefined
          }
          mode={modalState.mode}
          onClose={closeModal}
        />
      )}
    </Box>
  );
};
